import { Blocks, Bot, Cloud, Network } from "lucide-react";
import { Eyebrow, Reveal } from "../site/motion";

const areas = [
  { label: "Modern Architecture", icon: Network },
  { label: "Cloud-Native Infrastructure", icon: Cloud },
  { label: "API-First Integrations", icon: Blocks },
  { label: "Automation & AI", icon: Bot },
];

function CodeWindow() {
  return (
    <div className="relative">
      <div className="absolute -inset-8 rounded-[32px] bg-gradient-to-br from-blue-600/15 to-violet-600/20 blur-2xl" aria-hidden="true" />
      <div className="relative -rotate-1 overflow-hidden rounded-2xl border border-white/10 bg-navy shadow-[0_50px_90px_-45px_rgba(11,18,32,0.7)] transition-transform duration-500 hover:rotate-0">
        <div className="flex items-center gap-2 border-b border-white/[0.07] px-5 py-3.5">
          <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
          <span className="ml-3 font-mono text-[10px] tracking-[0.2em] text-slate-500">
            bytevora.config.ts
          </span>
        </div>
        <pre className="overflow-x-auto p-6 font-mono text-[13px] leading-7">
          <code>
            <span className="text-slate-500">{"// engineering principles"}</span>{"\n"}
            <span className="text-violet-400">export default</span>{" "}
            <span className="text-slate-300">{"{"}</span>{"\n"}
            {"  "}<span className="text-slate-200">architecture</span><span className="text-slate-500">: </span>
            <span className="text-blue-300">"modular"</span>
            <span className="text-slate-500">,</span>{"\n"}
            {"  "}<span className="text-slate-200">cloud</span><span className="text-slate-500">: </span>
            <span className="text-blue-300">"native"</span>
            <span className="text-slate-500">,</span>{"\n"}
            {"  "}<span className="text-slate-200">apis</span><span className="text-slate-500">: </span>
            <span className="text-blue-300">"first"</span>
            <span className="text-slate-500">,</span>{"\n"}
            {"  "}<span className="text-slate-200">intelligence</span><span className="text-slate-500">: [</span>
            <span className="text-blue-300">"ai"</span>
            <span className="text-slate-500">, </span>
            <span className="text-blue-300">"automation"</span>
            <span className="text-slate-500">]</span>
            <span className="text-slate-500">,</span>{"\n"}
            {"  "}<span className="text-slate-200">scale</span><span className="text-slate-500">: </span>
            <span className="text-violet-400">Infinity</span>
            <span className="text-slate-500">,</span>{"\n"}
            <span className="text-slate-300">{"}"}</span>
          </code>
        </pre>
      </div>
    </div>
  );
}

export default function Engineering() {
  return (
    <section
      data-testid="engineering-section"
      className="bg-[linear-gradient(180deg,#F8FAFC_0%,#EEF3FF_100%)] py-24 md:py-32"
    >
      <div className="mx-auto grid w-full max-w-[1200px] items-center gap-16 px-6 md:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Engineering approach</Eyebrow>
          <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.6rem]">
            The right technology for the right problem.
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-600 md:text-base">
            We don&rsquo;t define projects by frameworks. We choose technologies around
            performance, security, scalability, maintainability, and the long-term needs of
            the product.
          </p>
          <div className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {areas.map((a) => (
              <div
                key={a.label}
                data-testid={`engineering-area-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-3.5 backdrop-blur transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-blue-600/35"
              >
                <a.icon size={17} strokeWidth={1.8} className="text-blue-600" />
                <span className="text-sm font-medium text-slate-800">{a.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={1}>
          <CodeWindow />
        </Reveal>
      </div>
    </section>
  );
}
